import debug from './debug';
import LS, { LS_KEY } from './LocalStorage';

const SYNC_XHR_LS_PREFIX = 'syncXhr_';
const SYNC_XHR_TIME_TO_OLD_AGE_S = 10 * 1000;
/**
 * Enum for possible states of sync xhr support
 * @enum {string}
 * */
const SYNC_XHR_SUPPORT = {
    SUPPORTED: 'supported',
    UNSUPPORTED: 'unsupported',
    UNKNOWN: 'unknown'
};

const isSyncXhrRelatedLSProp = (p) => p.startsWith(SYNC_XHR_LS_PREFIX);

const isOldXhr = ({ date }) => new Date(date) < new Date(Date.now() + SYNC_XHR_TIME_TO_OLD_AGE_S);

const onProlongedXhr = (context) => debug.error('Sync XHR failure', context);

const handleOldIncompleteSyncXhrs = () => {
    const existingFailures = Object.entries(LS.getStorage())
        .filter(([key, val]) => isSyncXhrRelatedLSProp(key) && isOldXhr(val));

    existingFailures.forEach(([key, val]) => {
        onProlongedXhr({ key, val });
        LS.remove(key);
    });
};

export const startTrackingOldSyncXhrFailures = () => {
    handleOldIncompleteSyncXhrs();
    setInterval(handleOldIncompleteSyncXhrs, SYNC_XHR_TIME_TO_OLD_AGE_S);
};

/**
 *  @typedef {Object} MemorableSyncXhrResponse
 *  @property {XMLHttpRequest} request
 *  @property {number} responseTimeMs - the time it took for us to get the response
 */

/**
 * This method makes one synchronous XHR call.
 * Setting a timeout directly [is forbidden by spec]{@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/timeout}.
 * [Using a service worker to intercept]{@link https://stackoverflow.com/questions/35879844/can-service-workers-respond-to-synchronous-xhr-requests}.
 * does not work in most browsers, even though the spec says nothing about it.
 * To reduce the effects of an XHR call hanging the user's page,
 * before the actual request is made - we store the date in LS.
 * After the call finishes - we clear that date.
 * handleAnyOldSyncXhrFailures can be called manually to check and log failures
 *  @param {string} url - Will also be used as a key for LS
 *  @param {string} [method=GET]
 *
 *  @returns {MemorableSyncXhrResponse}
 * */
export const memorableSyncXhr = (url, prepareRequest = (r) => ['GET', r]) => {
    const now = new Date().toISOString();
    const hash = SYNC_XHR_LS_PREFIX + url;

    LS.set(hash, { date: now });
    const start = Date.now();
    const [method, request] = prepareRequest(new window.XMLHttpRequest());
    request.open(method, url, false);
    // Freeze...
    request.send(null);
    // ...continue
    const responseTimeMs = Date.now() - start;
    LS.remove(hash);

    return {
        responseTimeMs,
        request
    };
};

/**
 * Has side effects - makes an API call and stores data in LS
 *  */
export const checkSyncXhrSupport = () => {
    const current = LS.get(LS_KEY.syncXhrSupported);

    if (current !== null) {
        return;
    }

    const result = {
        supported: SYNC_XHR_SUPPORT.UNKNOWN,
        checkMs: 0 // 0 means no request has been performed
    };
    let syncXhrResp;

    try {
        syncXhrResp = memorableSyncXhr('/sync-xhr-check');
        result.checkMs = syncXhrResp.responseTimeMs;
    } catch (e) {
        debug.recordError('Sync XHR NOT supported', e.message);
        result.supported = SYNC_XHR_SUPPORT.UNSUPPORTED;
    }

    try {
        const success = JSON.parse(syncXhrResp.request.responseText)?.success;

        if (!success) {
            throw new Error('Incorrect JSON received: ' + syncXhrResp.request.responseText);
        }
        result.supported = SYNC_XHR_SUPPORT.SUPPORTED;
    } catch (e) {
        // Could potentially mean the Mirror service targeted did not have the endpoint for the check
        debug.recordError('Malformed sync xhr check API response', e.message);
    }

    LS.set(LS_KEY.syncXhrSupported, result);
};
